import React, { useEffect, useRef, useState, useCallback } from "react";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { getAllPages, sections } from "./data";

export default function DocsSearch({ onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const fuseRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Build Fuse index on mount
  useEffect(() => {
    const pages = getAllPages().map((p) => ({
      ...p,
      sectionLabel: sections[p.section]?.label ?? "",
      headingTexts: (p.content ?? [])
        .filter((block) => block.type === "heading")
        .map((block) => block.text)
        .join(" "),
    }));

    fuseRef.current = new Fuse(pages, {
      keys: [
        { name: "title", weight: 1.0 },
        { name: "subtitle", weight: 0.8 },
        { name: "sectionLabel", weight: 0.6 },
        { name: "headingTexts", weight: 0.4 },
      ],
      threshold: 0.4,
      includeMatches: true,
    });
  }, []);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleQuery = useCallback((value) => {
    setQuery(value);
    setSelectedIndex(0);
    if (!value.trim() || !fuseRef.current) {
      setResults([]);
      return;
    }
    const raw = fuseRef.current.search(value);
    setResults(raw.slice(0, 8));
  }, []);

  const navigateTo = useCallback(
    (page) => {
      navigate(`/docs/${page.section}/${page.slug}`);
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (results.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        navigateTo(results[selectedIndex].item);
      }
    },
    [results, selectedIndex, navigateTo, onClose]
  );

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      {/* Card */}
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleQuery(e.target.value)}
            placeholder="Search documentation…"
            className="flex-1 bg-transparent text-slate-800 placeholder-slate-400 text-sm outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-400 text-xs font-mono">
            ESC
          </kbd>
        </div>

        {/* Results / Empty state */}
        <div className="max-h-[28rem] overflow-y-auto">
          {query.trim() === "" ? (
            <p className="px-6 py-8 text-sm text-slate-400 text-center">
              Type to search across all documentation pages
            </p>
          ) : results.length === 0 ? (
            <p className="px-6 py-8 text-sm text-slate-500 text-center">
              No results for &ldquo;{query}&rdquo;
            </p>
          ) : (
            <ul className="py-2">
              {results.map(({ item }, idx) => {
                const isSelected = idx === selectedIndex;
                const Icon = item.icon;
                return (
                  <li key={`${item.section}/${item.slug}`}>
                    <button
                      type="button"
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isSelected ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      onClick={() => navigateTo(item)}
                    >
                      {/* Icon box */}
                      <div className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
                        {Icon ? (
                          <Icon className="w-4 h-4" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-slate-400 truncate">
                          {sections[item.section]?.label ?? item.section}
                          {item.subtitle ? ` · ${item.subtitle}` : ""}
                        </p>
                      </div>

                      {/* Arrow */}
                      <ArrowRight
                        className={`w-4 h-4 shrink-0 transition-opacity ${
                          isSelected ? "opacity-60 text-blue-500" : "opacity-0"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
